import { useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  DataTable,
  Icon,
  InputText,
  Loading,
  RadioPresets,
  SectionCard,
  TextArea,
  type ColumnDef,
} from "@/deps/ui-library";
import { api } from "@/api";
import {
  keys,
  useCheckoutOffer,
  useSubscriptionAccount,
  useSubscriptionChanges,
} from "@/api/queries";
import type {
  CheckoutBilling,
  CheckoutChoice,
  CheckoutOffer,
  CheckoutOrder,
  CheckoutPlan,
  PaymentMethod,
  PendingChange,
  SubscriptionAccount,
  SubscriptionBill,
} from "@/api/types";
import { formatDate } from "@/lib/dates";
import { formatAmount } from "@/lib/money";
import { ChangeDrawer } from "./ChangeDrawer";
import { ChangeNowDrawer } from "./ChangeNow";
import { salesCountry, taxIdProblem } from "@/lib/countries";
import { featureName } from "./Checkout";
import { PaymentPage } from "./PaymentPage";

type DrawerKind =
  | "plan"
  | "cycle"
  | "removeAddOn"
  | "cancel"
  | "billing"
  | { now: CheckoutChoice; title: string };

const money = (currency: string, amount: number) => `${currency} ${formatAmount(amount)}`;
const perCycleWord = (cycle: string) => (cycle === "YEARLY" ? "a year" : "a month");

function Row({
  label,
  children,
  action,
}: {
  label: string;
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="border-line-soft flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-b px-4 py-2.5 text-sm last:border-0">
      <span className="text-fg-muted">{label}</span>
      <span className="text-fg flex flex-wrap items-center gap-3">
        {children}
        {action}
      </span>
    </div>
  );
}

/** What a scheduled change says, on the page and before it's undone. */
function describe(
  change: PendingChange,
  offer: CheckoutOffer | undefined,
): { title: string; detail: string } {
  const on = formatDate(change.effectiveOn);
  const locks = change.losing.length
    ? `${change.losing.map(featureName).join(", ")} ${change.losing.length === 1 ? "locks" : "lock"} that day. Nothing posted is removed.`
    : "Nothing locks.";
  switch (change.kind) {
    case "DOWNGRADE":
      return {
        title: `Moves to ${offer?.plans.find((p) => p.code === change.planCode)?.name ?? change.planCode} on ${on}`,
        detail: locks,
      };
    case "REMOVE_ADD_ON":
      return {
        title: `${offer?.addOns.find((a) => a.code === change.addOnCode)?.name ?? change.addOnCode} is removed on ${on}`,
        detail: `It works as now until then. ${locks}`,
      };
    case "CYCLE":
      return {
        title: `Billing becomes ${change.cycle === "YEARLY" ? "yearly" : "monthly"} on ${on}`,
        detail: "The renewal on that day bills the new cycle.",
      };
    default:
      return {
        title: `The subscription ends on ${on}`,
        detail:
          "Full access until then, and no refund for the rest of the period. After that the books lock; an administrator can subscribe again or export them.",
      };
  }
}

/**
 * Settings › Subscription for an administrator of a paid subscription (suite ADR-0098, plan slice 10.6): the plan and
 * what it costs, the changes scheduled for the renewal with Undo, the modules and users, the billing details and the
 * bills, and cancelling. A lower plan, an add-on removed, another cycle and cancelling wait for the renewal and charge
 * nothing. A higher plan, an add-on added and more users apply at once and are paid for through the payment page:
 * the difference for the rest of the period already paid (suite ADR-0099).
 */
export function ManageSubscription({
  inUse,
  actions,
}: {
  inUse: number | undefined;
  actions: ReactNode;
}) {
  const { data: account, error, isPending } = useSubscriptionAccount(true);
  const { data: offer } = useCheckoutOffer();
  const [drawer, setDrawer] = useState<DrawerKind | null>(null);
  const changes = useSubscriptionChanges();
  const [refused, setRefused] = useState<string | null>(null);
  const [order, setOrder] = useState<CheckoutOrder | null>(null);
  // How the open order is placed again through another method (suite ADR-0102).
  const [replace, setReplace] = useState<
    ((method: PaymentMethod) => Promise<CheckoutOrder>) | null
  >(null);
  const open = (
    placed: CheckoutOrder,
    again: (method: PaymentMethod) => Promise<CheckoutOrder>,
  ) => {
    setReplace(() => again);
    setOrder(placed);
  };
  const [paid, setPaid] = useState<string | null>(null);
  const queryClient = useQueryClient();

  if (isPending) return <Loading />;
  if (error || !account) {
    return (
      <Alert
        config={{ variant: "subtle", colorScheme: "danger" }}
        title="The subscription couldn't be loaded"
        description={error instanceof Error ? error.message : undefined}
      />
    );
  }

  const plan = offer?.plans.find((p) => p.code === account.current.planCode);
  const cancelling = account.cancelsOn !== null;
  const pendingOf = (kind: PendingChange["kind"]) => account.pending.find((c) => c.kind === kind);
  const cycle = account.current.cycle;
  const seats = account.includedUsers + account.current.extraUsers;
  // A drawer closes itself only: one that hands over to another (a higher plan to its payment) leaves that one open.
  const close = () => setDrawer((open) => (typeof open === "string" ? null : open));
  const closeNow = () => setDrawer((open) => (typeof open === "object" ? null : open));
  const changeNow = (title: string, next: Partial<CheckoutChoice>) => {
    setPaid(null);
    setDrawer({ title, now: { ...account.current, addOns: [...account.current.addOns], ...next } });
  };
  const upgrade = (planCode: string) => {
    const higher = offer?.plans.find((p) => p.code === planCode);
    if (!higher) return;
    changeNow(`Upgrade to ${higher.name}`, {
      planCode,
      addOns: account.current.addOns.filter((code) =>
        offer?.addOns.some((a) => a.code === code && a.prices[planCode]),
      ),
      extraUsers: higher.extraUser ? Math.max(0, seats - higher.includedUsers) : 0,
    });
  };

  const undo = (change: PendingChange) => {
    setRefused(null);
    changes.undo.mutate(change.id, {
      onError: (failure) =>
        setRefused(failure instanceof Error ? failure.message : "That couldn't be undone."),
    });
  };

  return (
    <div className="flex flex-col gap-4">
      {refused ? (
        <Alert config={{ variant: "subtle", colorScheme: "danger" }} description={refused} />
      ) : null}
      {paid ? (
        <Alert
          config={{ variant: "subtle", colorScheme: "success" }}
          title="Payment received"
          description={paid}
        />
      ) : null}

      <SectionCard
        title="Plan"
        actions={
          <span className="flex items-center gap-2">
            {actions}
            {!cancelling ? (
              <Button
                colorScheme="default"
                variant="secondary"
                size="sm"
                onClick={() => setDrawer("plan")}
              >
                Change plan
              </Button>
            ) : null}
          </span>
        }
        flush
      >
        <Row label="Status">
          <Badge colorScheme={cancelling ? "warning" : "success"} size="sm">
            {cancelling ? "Cancelling" : "Active"}
          </Badge>
        </Row>
        <Row label="Plan">{plan?.name ?? account.current.planCode}</Row>
        <Row
          label="Billing cycle"
          action={
            !cancelling && !pendingOf("CYCLE") ? (
              <Button
                colorScheme="default"
                variant="ghost"
                size="xs"
                onClick={() => setDrawer("cycle")}
              >
                {cycle === "YEARLY" ? "Switch to monthly" : "Switch to yearly"}
              </Button>
            ) : null
          }
        >
          {cycle === "YEARLY" ? "Yearly" : "Monthly"}
        </Row>
        {cancelling ? (
          <Row label="Access until">{formatDate(addDays(account.cancelsOn!, -1))}</Row>
        ) : account.renewsOn ? (
          <Row label="Next renewal">
            {`${formatDate(account.renewsOn)} · ${money(account.currency, account.afterRenewal?.perCycle ?? account.perCycle)} ${perCycleWord(account.afterRenewal?.terms.cycle ?? cycle)}${account.taxPercent > 0 ? " plus VAT" : ""}`}
          </Row>
        ) : null}
      </SectionCard>

      {account.pending.length > 0 ? (
        <SectionCard title="Scheduled for the renewal" flush>
          {account.pending.map((change) => {
            const { title, detail } = describe(change, offer);
            return (
              <div
                key={change.id}
                className="border-line-soft flex flex-wrap items-start justify-between gap-3 border-b px-4 py-3 last:border-0"
              >
                <span className="flex items-start gap-3">
                  <span className="shrink-0">
                    <Icon
                      name={change.kind === "CANCEL" ? "CalendarX" : "CalendarClock"}
                      size="sm"
                    />
                  </span>
                  <span className="flex flex-col gap-0.5">
                    <span className="text-fg text-sm font-semibold">{title}</span>
                    <span className="text-fg-muted text-sm">{detail}</span>
                  </span>
                </span>
                <Button
                  colorScheme="default"
                  variant="outline"
                  size="sm"
                  loading={changes.undo.isPending && changes.undo.variables === change.id}
                  onClick={() => undo(change)}
                >
                  {change.kind === "CANCEL" ? "Keep subscription" : "Undo"}
                </Button>
              </div>
            );
          })}
        </SectionCard>
      ) : null}

      <SectionCard title="Modules" flush>
        <Row label="Core accounting">{plan?.name ?? account.current.planCode}</Row>
        {(offer?.addOns ?? []).map((addOn) => {
          const held = account.current.addOns.includes(addOn.code);
          const price = addOn.prices[account.current.planCode];
          return (
            <Row
              key={addOn.code}
              label={addOn.name}
              action={
                cancelling ? null : held ? (
                  pendingOf("REMOVE_ADD_ON") ? null : (
                    <Button
                      colorScheme="default"
                      variant="ghost"
                      size="xs"
                      onClick={() => setDrawer("removeAddOn")}
                    >
                      Remove
                    </Button>
                  )
                ) : price ? (
                  <Button
                    colorScheme="default"
                    variant="ghost"
                    size="xs"
                    onClick={() =>
                      changeNow(`Add the ${addOn.name}`, {
                        addOns: [...account.current.addOns, addOn.code],
                      })
                    }
                  >
                    Add
                  </Button>
                ) : null
              }
            >
              {held
                ? "Added"
                : price
                  ? `Not added · ${money(account.currency, cycle === "YEARLY" ? price.yearly : price.monthly)} ${perCycleWord(cycle)}`
                  : "Not added"}
            </Row>
          );
        })}
      </SectionCard>

      <SectionCard title="Users" flush>
        <Row
          label="Users"
          action={
            plan?.extraUser && !cancelling ? (
              <Button
                colorScheme="default"
                variant="ghost"
                size="xs"
                onClick={() =>
                  changeNow("Add users", { extraUsers: account.current.extraUsers + 1 })
                }
              >
                Add users
              </Button>
            ) : null
          }
        >
          {`${inUse !== undefined ? `${inUse} of ${seats}` : `${seats} seats`} · ${account.includedUsers} included${account.current.extraUsers ? ` + ${account.current.extraUsers} extra` : ""}`}
        </Row>
      </SectionCard>

      <Billing
        account={account}
        onEdit={() => setDrawer("billing")}
        onPay={(billId) => {
          setRefused(null);
          setPaid(null);
          // A bill still owed is paid on the payment page (plan slice 10.7).
          api.subscription
            .payBill(billId)
            .then((placed) => open(placed, (method) => api.subscription.payBill(billId, method)))
            .catch((failure: unknown) =>
              setRefused(
                failure instanceof Error ? failure.message : "The payment couldn't be started.",
              ),
            );
        }}
      />

      {!cancelling ? (
        <div>
          <Button
            colorScheme="danger"
            variant="ghost"
            size="sm"
            onClick={() => setDrawer("cancel")}
          >
            Cancel subscription
          </Button>
        </div>
      ) : null}

      {typeof drawer === "object" && drawer !== null && offer ? (
        <ChangeNowDrawer
          key={drawer.title}
          title={drawer.title}
          account={account}
          offer={offer}
          start={drawer.now}
          onOrder={open}
          onClose={closeNow}
        />
      ) : drawer === "plan" && offer ? (
        <PlanDrawer
          account={account}
          offer={offer}
          inUse={inUse}
          onUpgrade={upgrade}
          onClose={close}
        />
      ) : drawer === "cycle" && offer ? (
        <CycleDrawer account={account} offer={offer} onClose={close} />
      ) : drawer === "removeAddOn" && offer ? (
        <RemoveAddOnDrawer account={account} offer={offer} onClose={close} />
      ) : drawer === "cancel" ? (
        <CancelDrawer account={account} onClose={close} />
      ) : drawer === "billing" ? (
        <BillingDrawer billing={account.billing} country={account.country} onClose={close} />
      ) : null}

      {order ? (
        <PaymentPage
          order={order}
          replace={replace ?? undefined}
          onClose={() => setOrder(null)}
          onPaid={() => {
            setOrder(null);
            setPaid(
              order.quote.kind === "PAY_BILL"
                ? `${order.quote.lines.map((line) => line.label).join(", ")} is paid. The tax invoice comes from Rigo Billing.`
                : `${order.quote.lines.map((line) => line.label).join(", ")}. The tax invoice comes from Rigo Billing, and the bill is listed below.`,
            );
            void queryClient.invalidateQueries({ queryKey: keys.subscription });
          }}
        />
      ) : null}
    </div>
  );
}

function addDays(iso: string, days: number): string {
  const date = new Date(`${iso}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

function Billing({
  account,
  onEdit,
  onPay,
}: {
  account: SubscriptionAccount;
  onEdit: () => void;
  onPay: (billId: string) => void;
}) {
  const columns: Array<ColumnDef<SubscriptionBill>> = [
    {
      id: "bill",
      header: "Bill",
      meta: { numeric: true },
      cell: ({ row: { original: bill } }) => bill.id,
    },
    {
      id: "issued",
      header: "Issued",
      cell: ({ row: { original: bill } }) => formatDate(bill.issueDate),
    },
    { id: "period", header: "Period", cell: ({ row: { original: bill } }) => bill.period },
    {
      id: "amount",
      header: "Amount",
      meta: { numeric: true },
      cell: ({ row: { original: bill } }) => formatAmount(bill.total),
    },
    {
      id: "status",
      header: "Status",
      cell: ({ row: { original: bill } }) => (
        <Badge colorScheme={bill.balance > 0 ? "warning" : "success"} size="sm">
          {bill.balance > 0 ? "Due" : "Paid"}
        </Badge>
      ),
    },
    {
      id: "pay",
      header: "",
      cell: ({ row: { original: bill } }) =>
        bill.balance > 0 ? (
          <Button
            colorScheme="default"
            variant="secondary"
            size="xs"
            onClick={() => onPay(bill.id)}
          >
            Pay
          </Button>
        ) : null,
    },
  ];
  const b = account.billing;
  return (
    <SectionCard
      title="Billing"
      actions={
        <Button colorScheme="default" variant="secondary" size="sm" onClick={onEdit}>
          Edit billing details
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        <div className="text-fg flex flex-col gap-0.5 text-sm">
          <span className="font-semibold">{b.name}</span>
          {b.taxId ? (
            <span>{`${salesCountry(account.country).idLabel.replace(" (optional)", "")} ${b.taxId}`}</span>
          ) : null}
          {account.country ? (
            <span className="text-fg-muted">{`${salesCountry(account.country).name} · billed in ${account.currency}, which can't be changed`}</span>
          ) : null}
          {b.address ? <span>{b.address}</span> : null}
          {b.email ? <span className="text-fg-muted">{`Invoices to ${b.email}`}</span> : null}
        </div>
        <DataTable
          aria-label="Bills"
          size="sm"
          enableSorting={false}
          columns={columns}
          data={account.bills}
          getRowId={(bill) => bill.id}
          emptyContent="No bills yet."
          pagination={{ mode: "manual", show: false }}
        />
        <p className="text-fg-muted m-0 text-xs">
          {account.taxPercent > 0
            ? `Amounts are before VAT (${account.taxPercent}%). The tax invoices come from Rigo Billing.`
            : `Amounts are in ${account.currency}, with no VAT. The invoices come from Rigo Billing.`}
        </p>
      </div>
    </SectionCard>
  );
}

function PlanDrawer({
  account,
  offer,
  inUse,
  onUpgrade,
  onClose,
}: {
  account: SubscriptionAccount;
  offer: CheckoutOffer;
  inUse: number | undefined;
  onUpgrade: (planCode: string) => void;
  onClose: () => void;
}) {
  const changes = useSubscriptionChanges();
  const current = offer.plans.find((p) => p.code === account.current.planCode);
  const [target, setTarget] = useState(account.current.planCode);
  const [accepted, setAccepted] = useState(false);
  const chosen = offer.plans.find((p) => p.code === target);
  const same = target === account.current.planCode;
  const higher = !!chosen && !!current && chosen.price.monthly > current.price.monthly;
  const yearly = account.current.cycle === "YEARLY";
  const renewal = account.renewsOn ? formatDate(account.renewsOn) : "the renewal";

  // What the trial-free subscription holds and what the lower plan would: names only, no money.
  const featuresOf = (p: CheckoutPlan | undefined, addOns: string[]) =>
    new Set([
      ...(p?.features ?? []),
      ...addOns.flatMap((code) => offer.addOns.find((a) => a.code === code)?.features ?? []),
    ]);
  const keptAddOns = account.current.addOns.filter((code) =>
    offer.addOns.some((a) => a.code === code && chosen && a.prices[chosen.code]),
  );
  const held = featuresOf(current, account.current.addOns);
  const after = featuresOf(chosen, keptAddOns);
  const losing = [...held].filter((code) => !after.has(code));
  const room = chosen
    ? chosen.includedUsers + (chosen.extraUser ? account.current.extraUsers : 0)
    : 0;
  const tooMany = !!chosen && !higher && inUse !== undefined && inUse > room;

  return (
    <ChangeDrawer
      title="Change plan"
      description={`A lower plan takes effect at the renewal, ${renewal}. A higher plan applies straight away.`}
      rows={
        same || !chosen
          ? []
          : higher
            ? [
                { label: "Takes effect", value: "Straight away" },
                {
                  label: "Charged now",
                  value: `The difference to ${renewal}, by the day. You see it before you pay.`,
                },
              ]
            : [
                { label: "Takes effect", value: `${renewal}, at the renewal` },
                { label: "Charged now", value: "Nothing" },
                {
                  label: `${chosen.name} from ${renewal}`,
                  value: `${money(offer.currency, yearly ? chosen.price.yearly : chosen.price.monthly)} ${perCycleWord(account.current.cycle)}${account.taxPercent > 0 ? " plus VAT" : ""}, before add-ons`,
                },
              ]
      }
      confirm={{
        label: same
          ? "Current plan"
          : higher
            ? "Continue"
            : `Move to ${chosen?.name} on ${renewal}`,
        disabled: same || (!higher && (tooMany || (losing.length > 0 && !accepted))),
        onConfirm: async () => {
          if (higher) {
            onUpgrade(target);
            return;
          }
          await changes.schedule.mutateAsync({ kind: "DOWNGRADE", planCode: target });
        },
      }}
      onClose={onClose}
    >
      <RadioPresets.CardRadio
        aria-label="Plan"
        value={target}
        onValueChange={(details: { value: string | null }) => {
          if (details.value) {
            setTarget(details.value);
            setAccepted(false);
          }
        }}
        items={offer.plans.map((p) => ({
          value: p.code,
          label: `${p.name} · ${money(offer.currency, yearly ? p.price.yearly : p.price.monthly)} ${perCycleWord(account.current.cycle)}`,
          description: `${p.includedUsers} users included${p.code === account.current.planCode ? ". Your plan" : ""}`,
        }))}
      />
      {tooMany ? (
        <Alert
          config={{ variant: "subtle", colorScheme: "warning" }}
          description={`${inUse} users are active, and ${chosen?.name} has room for ${room}. Remove users in Settings › Users first.`}
        />
      ) : null}
      {!same && !higher && losing.length > 0 ? (
        <Alert
          config={{ variant: "subtle", colorScheme: "warning" }}
          title={`On ${renewal} these lock: ${losing.map(featureName).join(", ")}`}
          description={
            <Checkbox
              label={`I understand these lock on ${renewal}`}
              checked={accepted}
              onCheckedChange={(details) => setAccepted(details.checked === true)}
            />
          }
        />
      ) : null}
    </ChangeDrawer>
  );
}

function CycleDrawer({
  account,
  offer,
  onClose,
}: {
  account: SubscriptionAccount;
  offer: CheckoutOffer;
  onClose: () => void;
}) {
  const changes = useSubscriptionChanges();
  const toYearly = account.current.cycle !== "YEARLY";
  const renewal = account.renewsOn ? formatDate(account.renewsOn) : "the renewal";
  const plan = offer.plans.find((p) => p.code === account.current.planCode);
  const price = (p: { monthly: number; yearly: number }) =>
    `${money(offer.currency, toYearly ? p.yearly : p.monthly)} ${toYearly ? "a year" : "a month"}`;
  const rows = [
    { label: "Takes effect", value: `${renewal}, at the renewal` },
    ...(plan ? [{ label: plan.name, value: price(plan.price) }] : []),
    ...account.current.addOns.flatMap((code) => {
      const addOn = offer.addOns.find((a) => a.code === code);
      const p = addOn?.prices[account.current.planCode];
      return addOn && p ? [{ label: addOn.name, value: price(p) }] : [];
    }),
    ...(plan?.extraUser && account.current.extraUsers > 0
      ? [
          {
            label: `Each of ${account.current.extraUsers} extra users`,
            value: price(plan.extraUser),
          },
        ]
      : []),
  ];
  return (
    <ChangeDrawer
      title={toYearly ? "Switch to yearly billing" : "Switch to monthly billing"}
      description={
        toYearly
          ? "The yearly prices are 15% off twelve months, from the next renewal."
          : "Monthly billing from the next renewal, at the monthly prices."
      }
      rows={rows}
      confirm={{
        label: `Switch on ${renewal}`,
        onConfirm: () =>
          changes.schedule.mutateAsync({ kind: "CYCLE", cycle: toYearly ? "YEARLY" : "MONTHLY" }),
      }}
      onClose={onClose}
    >
      <p className="text-fg-muted m-0 text-sm">{`Billing stays ${toYearly ? "monthly" : "yearly"} until ${renewal}. All prices are before VAT.`}</p>
    </ChangeDrawer>
  );
}

function RemoveAddOnDrawer({
  account,
  offer,
  onClose,
}: {
  account: SubscriptionAccount;
  offer: CheckoutOffer;
  onClose: () => void;
}) {
  const changes = useSubscriptionChanges();
  const addOn = offer.addOns.find((a) => account.current.addOns.includes(a.code));
  const renewal = account.renewsOn ? formatDate(account.renewsOn) : "the renewal";
  if (!addOn) return null;
  return (
    <ChangeDrawer
      title={`Remove ${addOn.name}`}
      description={`Takes effect at the next renewal, ${renewal}.`}
      rows={[
        { label: "Takes effect", value: `${renewal}, at the renewal` },
        { label: "Charged now", value: "Nothing" },
      ]}
      confirm={{
        label: `Remove on ${renewal}`,
        danger: true,
        onConfirm: () =>
          changes.schedule.mutateAsync({ kind: "REMOVE_ADD_ON", addOnCode: addOn.code }),
      }}
      onClose={onClose}
    >
      <p className="text-fg m-0 text-sm">
        {`Until ${renewal} everything works as now. After that, ${addOn.features.map(featureName).join(", ")} lock. What it holds is kept, and adding it back restores it.`}
      </p>
    </ChangeDrawer>
  );
}

function CancelDrawer({ account, onClose }: { account: SubscriptionAccount; onClose: () => void }) {
  const changes = useSubscriptionChanges();
  const [accepted, setAccepted] = useState(false);
  const renewal = account.renewsOn ? formatDate(account.renewsOn) : "the renewal";
  const lastDay = account.renewsOn
    ? formatDate(addDays(account.renewsOn, -1))
    : "the end of the period";
  return (
    <ChangeDrawer
      title="Cancel subscription"
      description="The subscription runs to the end of the paid period."
      rows={[
        { label: "Access until", value: lastDay },
        { label: "Charged now", value: "Nothing, and no refund for the rest of the period" },
        {
          label: `From ${renewal}`,
          value: "The books lock. An administrator can subscribe again or export them.",
        },
      ]}
      confirm={{
        label: "Cancel subscription",
        danger: true,
        disabled: !accepted,
        onConfirm: () => changes.schedule.mutateAsync({ kind: "CANCEL" }),
      }}
      onClose={onClose}
      dismissLabel="Keep subscription"
    >
      <Checkbox
        label={`I understand the books lock on ${renewal}`}
        checked={accepted}
        onCheckedChange={(details) => setAccepted(details.checked === true)}
      />
    </ChangeDrawer>
  );
}

function BillingDrawer({
  billing,
  country,
  onClose,
}: {
  billing: CheckoutBilling;
  country: string | null | undefined;
  onClose: () => void;
}) {
  const changes = useSubscriptionChanges();
  const [draft, setDraft] = useState<CheckoutBilling>(billing);
  const nameMissing = !draft.name.trim();
  const emailMissing = !draft.email?.trim();
  // The tax ID is the billing country's (suite ADR-0101); the country itself is fixed once subscribed.
  const id = salesCountry(country);
  const idWrong = draft.taxId?.trim() ? taxIdProblem(country, draft.taxId) : null;
  const panWrong = idWrong !== null;
  return (
    <ChangeDrawer
      title="Billing details"
      description="Used on the next tax invoice from Rigo. Invoices already issued keep theirs."
      rows={[]}
      confirm={{
        label: "Save",
        disabled: nameMissing || emailMissing || panWrong,
        onConfirm: () =>
          changes.billing.mutateAsync({
            name: draft.name.trim(),
            taxId: draft.taxId?.trim() || null,
            address: draft.address?.trim() || null,
            email: draft.email?.trim() || null,
          }),
      }}
      onClose={onClose}
    >
      <InputText
        label="Legal name"
        required
        value={draft.name}
        onChange={(e) => setDraft({ ...draft, name: e.target.value })}
        errorText={nameMissing ? "The legal name goes on the tax invoice." : undefined}
      />
      <InputText
        label={id.idLabel}
        helperText={id.idHint || undefined}
        inputMode={id.digits ? "numeric" : undefined}
        value={draft.taxId ?? ""}
        onChange={(e) => setDraft({ ...draft, taxId: e.target.value })}
        errorText={idWrong ?? undefined}
      />
      <TextArea
        label="Billing address"
        rows={2}
        value={draft.address ?? ""}
        onChange={(e) => setDraft({ ...draft, address: e.target.value })}
      />
      <InputText
        label="Billing email"
        type="email"
        required
        value={draft.email ?? ""}
        onChange={(e) => setDraft({ ...draft, email: e.target.value })}
        errorText={emailMissing ? "Invoices are sent here." : undefined}
      />
    </ChangeDrawer>
  );
}
